/* eslint-disable @typescript-eslint/no-unused-vars */
import { mapDatacenterDetailToSections } from "kontrolEdge/constants/constants";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import DataCenterDetailsCard from "./DatacenterDetailsCard";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { RootState } from "@redux/store/store";
import { ServiceDetailsPageSkeleton } from "kontrolEdge/components/Shared/ServiceDetailsPageSkeleton";
import { fetchDatacenterDetailAPI, fetchDatacenterErrorsAPI, fetchDatacenterSubscriptionAPI } from "../requests/request";
import BandwidthChartUniversal from "kontrolEdge/components/Shared/Bandwidth/BandwidthChartUniversal";
import { apiTagsToSummaries } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";
import { useDetailTenantId } from "@shared/navigation/tabs/useDetailTenantId";
import { readCachedFetch, writeCachedFetch } from "@hooks/useCachedFetch";

// 1) The overall response shape
export interface DatacenterResponse {
  success: boolean;
  message: string;
  data: RawDatacenterDetail;
}

export interface Subnet {
  id: number;
  ip: string;
  mask: string;
  status: string;
}
export interface SubscriptionDetail {
  subscriptionId: string;
}
export interface RawDatacenterDetail {
  id: number;
  mbps: number;
  vlan: number;
  automaticVlan: boolean;
  redundancy: boolean;
  routing: string;
  terms: number;
  autoRenew: boolean;
  subscriptionId: string;
  port: {
	id: number;
	name: string;
	datacenter: {
	datacenterId: string;
	name: string;
	displayName: string;
	direction: string;
	gps: string;
	capacityKW: string;
	phase: string;
	cage: string;
	rackId: string;
	rackCapacity: string;
	patchPanelODF: string;
	rackSecurityPin: string;
	status: string;
	visible: boolean;
	providerCode: string;
	city: string;
	providerId: number;
	createdAt: string;
	updatedAt: string;
	};
  };
  lag: null;
  performanceMetrics?: {
	errors: number;
  };
  subscription?: SubscriptionDetail;
  subnets: Array<{
	id: number;
	ip: string;
	mask: string;
	status: string;
  }>;
}

/**
 * First-paint shortcut from the list, never a requirement: the detail fetch below is what
 * the page renders, so a pasted URL paints the same screen (CTX-773). The tenant the
 * connection is read in travels in `?tenantId=` — see `useDetailTenantId`.
 */
type LocationState = { datacenterDetail?: RawDatacenterDetail };
  
const DatacenterDetails: React.FC = () => {
	//Grab the raw Location object
	const location = useLocation();
	const reduxTenantId = useAppSelector((state) => state.selection.tenantId);
	//Assert its state shape
	const { datacenterDetail: preloaded } = (location.state as LocationState | null) ?? {};
	const [searchParams] = useSearchParams();
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const navigate = useNavigate();

	//Port id selected from MainPort
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const datacenterId = searchParams.get("datacenterId")!;
	//Seed from navigation‑state if available
	const effectiveTenantId = useDetailTenantId();
	// The last assembled answer for this resource paints at once when the user comes back
	// (a view switch, Back); the requests below still run and replace it.
	const detailCacheKey = datacenterId ? `kontroledge/datacenter-interconnect/${datacenterId}?tenantId=${effectiveTenantId}` : null;
	const [detail, setDetail] = useState<RawDatacenterDetail | undefined>(
		() => readCachedFetch<RawDatacenterDetail>(detailCacheKey) ?? preloaded
	);
	// Starts as fetching: with no row to seed from, the first frame would otherwise read
	// "not found" before the fetch effect has had a chance to run.
	const [isFetching, setIsFetching] = useState(() => Boolean(datacenterId) && !readCachedFetch(detailCacheKey));
	const api = useAxiosAuth0();
	const { tenantId } = useAppSelector(state => state.selection);
	const userProfile = useAppSelector((state: RootState) => state.userProfile);

		
	// Track the initial tenantId when component mounts to detect changes
	const initialTenantIdRef = useRef<number | null>(null);
	
	// Initialize the ref on mount
	useEffect(() => {
		if (initialTenantIdRef.current === null) {
			initialTenantIdRef.current = reduxTenantId;
		}
	}, []);

	// Redirect to main view if tenantId changes while on detail page
	useEffect(() => {
		// Skip if ref hasn't been initialized yet
		if (initialTenantIdRef.current === null) return;
		
		// Only redirect if tenantId actually changed (not on initial mount)
		if (reduxTenantId !== initialTenantIdRef.current) {
			navigate("/kontrol/datacenter");
		}
	}, [reduxTenantId, navigate]);

	const sections = useMemo(
		() => mapDatacenterDetailToSections({ data: detail }),
		[detail]
	);

	// Rich tags inline from the detail GET (§5.5). Forwarded to the card so
	// `useResourceTags` skips its first §5.1 round-trip when the modal opens.
	const initialAssignedTags = useMemo(
		() => apiTagsToSummaries((detail as { tags?: unknown } | undefined)?.tags),
		[detail]
	);

	// Tenant the resource actually lives in. See PortDetails.tsx for the
	// rationale (partner-view writes need this).
	//
	// Falls back to `effectiveTenantId` (the same value the detail GET
	// sent as `?tenantId=`) when `raw.tenantId` is not exposed on the
	// response body — keeps the picker / attach calls scoped to the
	// resource's tenant instead of leaking to the JWT tenant.
	const resourceTenantId = useMemo(() => {
		const t = (detail as { tenantId?: unknown } | undefined)?.tenantId;
		if (typeof t === "number" && Number.isFinite(t)) return t;
		return effectiveTenantId ?? undefined;
	}, [detail, effectiveTenantId]);
		useEffect(() => {
		//Re-run whenever datacenterId or refresh changes
		if (!datacenterId) return;
		let cancelled = false;
		// Only a first visit waits on a skeleton; a cached answer stays up while this refreshes.
		if (!readCachedFetch(detailCacheKey)) setIsFetching(true);
	
		(async () => {
		try {
			//Core detail – show it immediately fetchDatacenterDetailAPI
			const coreResp = await fetchDatacenterDetailAPI(api, datacenterId, effectiveTenantId);
			const fresh: RawDatacenterDetail = coreResp.data.data;
			if (cancelled) return;
			setDetail(fresh);
	
			//Extras in parallel
			const [
			errorsSettled,
			subscriptionSettled
			] = await Promise.allSettled([
			fetchDatacenterErrorsAPI(api, datacenterId, tenantId),
			fetchDatacenterSubscriptionAPI(api, datacenterId, tenantId),
			]);
	
			//Merge successful results
			if (errorsSettled.status === 'fulfilled') {
			fresh.performanceMetrics = {
				...fresh.performanceMetrics,
				errors: errorsSettled.value.data.data.crcErrors
			};
			} else {
			console.warn('CRC errors failed:', errorsSettled.reason);
			}

			if (subscriptionSettled.status === 'fulfilled') {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(fresh as any).subscription = subscriptionSettled.value.data.data;
			} else {
			console.warn('Subscription fetch failed:', subscriptionSettled.reason);
			}
	
			if (cancelled) return;
			//Push the merged object back into state
			writeCachedFetch(detailCacheKey, { ...fresh });
			setDetail({ ...fresh });
	
		} catch (err) {
			console.error('Error loading InternetDetails:', err);
		} finally {
			if (!cancelled) setIsFetching(false);
		}
		})();
	
		return () => { cancelled = true; };
	}, [api, datacenterId]);
		
		if (isFetching) {
			return <ServiceDetailsPageSkeleton />;
		}
		if (!detail) {
			return (
				<p className="p-4 text-sm text-semantic-text-secondary">
					Datacenter connection not found or could not be loaded.
				</p>
			);
		}

	return (
		<>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 auto-rows-auto">
			{sections.map((section, idx) => (
				<DataCenterDetailsCard key={idx} data={section} tenantId={effectiveTenantId} initialAssignedTags={initialAssignedTags} resourceTenantId={resourceTenantId}/>
			))}
			</div>
			{/* Bandwidth Utilization chart */}
			<BandwidthChartUniversal 
				service="datacenter"
				id={datacenterId}
				tenantId={effectiveTenantId}
				portType="physical"
			/>
		</>
	);
};

export default DatacenterDetails;
